import { css } from '@styled-system/css';
import { type FC, type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '@/shared/hooks/useIsMobile';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number | string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  footer?: ReactNode;
  hideFooter?: boolean;
}

const backdrop = css({
  position: 'fixed',
  inset: 0,
  bg: 'rgba(0, 0, 0, 0.45)',
  zIndex: 1100,
  animation: 'fadeIn 160ms ease-out',
});

const panelBase = css({
  position: 'fixed',
  bg: 'surface.elevated',
  color: 'text.primary',
  boxShadow: 'lg',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1101,
  borderColor: 'border.default',
});

const closeBtn = css({
  position: 'absolute',
  top: '10',
  right: '10',
  width: '32px',
  height: '32px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: 'surface.sunken',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  color: 'text.secondary',
  borderRadius: 'md',
  cursor: 'pointer',
  zIndex: 1,
  boxShadow: 'xs',
  transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  _hover: { bg: 'border.default', color: 'text.primary', borderColor: 'border.strong' },
});

const body = css({
  flex: 1,
  overflow: 'auto',
  padding: 0,
});

const footerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  paddingY: '6',
  paddingX: '12',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'border.default',
  flexShrink: 0,
});

const placementStyle = (placement: DrawerProps['placement'], width: number | string): React.CSSProperties => {
  switch (placement) {
    case 'left':
      return { top: 0, left: 0, bottom: 0, width: typeof width === 'number' ? `${width}px` : width, borderRightWidth: '1px', borderRightStyle: 'solid', animation: 'slideInLeft 180ms ease-out' };
    case 'top':
      return { top: 0, left: 0, right: 0, height: typeof width === 'number' ? `${width}px` : width, borderBottomWidth: '1px', borderBottomStyle: 'solid', animation: 'slideInTop 180ms ease-out' };
    case 'bottom':
      return { bottom: 0, left: 0, right: 0, height: typeof width === 'number' ? `${width}px` : width, borderTopWidth: '1px', borderTopStyle: 'solid', animation: 'slideInBottom 180ms ease-out' };
    case 'right':
    default:
      return { top: 0, right: 0, bottom: 0, width: typeof width === 'number' ? `${width}px` : width, borderLeftWidth: '1px', borderLeftStyle: 'solid', animation: 'slideInRight 180ms ease-out' };
  }
};

export const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  children,
  width = 480,
  placement = 'right',
  footer,
  hideFooter = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Mobile : forcer le drawer en plein écran (100vw / 100dvh) — sinon les
  // panneaux 480px débordent ou laissent un overlay inutile sur smartphone.
  const effectiveWidth = isMobile ? '100vw' : width;

  return createPortal(
    <>
      <div className={backdrop} onClick={onClose} />
      <div className={panelBase} style={placementStyle(placement, effectiveWidth)} role="dialog" aria-modal="true">
        <button type="button" className={closeBtn} onClick={onClose} aria-label={t('common.close')}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <div className={body}>{children}</div>
        {!hideFooter && footer && <div className={footerStyles}>{footer}</div>}
      </div>
    </>,
    document.body,
  );
};
